import type { ColorwayOption } from '@apps/card/routes/CardCustomizer/CardCustomizer.types';
import { swatchColorFromColorway } from '@apps/card/utils/colorway';
import { useTranslation } from '@shared/core';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { Box, Flex, TextV2, TooltipV2 } from '@withjoy/joykit';
import React from 'react';

type Props = Readonly<{
  options: ReadonlyArray<ColorwayOption>;
  selectedId: string;
  onSelect: (id: string) => void;
  size?: SwatchSize;
  /**
   * 'wrap' (default): render every swatch, wrapping to the next line.
   * 'overflow': clamp to a responsive max (see the useResponsive call) and
   * append a "+N" remaining-count label that fires `onOverflowClick`.
   */
  variant?: 'wrap' | 'overflow';
  onOverflowClick?: () => void;
  /** Show a tooltip on each swatch with the capitalized colorwayId. Hidden by default. */
  showTooltip?: boolean;
}>;

const SWATCH_SIZE = { small: 20, large: 38 } as const;

type SwatchSize = keyof typeof SWATCH_SIZE;

export const CardColorSwatches = ({ options, selectedId, onSelect, size = 'small', variant = 'wrap', onOverflowClick, showTooltip = false }: Props) => {
  const { tq } = useTranslation('stationery');
  const swatchSize = SWATCH_SIZE[size];
  // Max swatches before collapsing into the overflow chip; grows where there is
  // room, stepping at the same breakpoints the gallery grid uses (md3 >= 1280px
  // → 4 columns, xl >= 1920px → 6 columns). initialValue keeps SSR/first-paint
  // at the narrow count.
  const [maxVisible] = useResponsive({ values: { mobile: 4, md3: 6, xl: 8 } }, 4);
  // Collapse into the "+N" label only when it stands in for 2+ hidden colors. A
  // single hidden colorway would show as "+1", which is barely worth a click
  // (and the click navigates to the PDP) — so in that case show them all.
  const isClamped = variant === 'overflow' && !!onOverflowClick && options.length > maxVisible + 1;
  const visibleOptions = isClamped ? options.slice(0, maxVisible) : options;
  const hiddenCount = isClamped ? options.length - maxVisible : 0;
  return (
    <Flex
      flexDirection="row"
      alignItems="center"
      gap={3}
      flexWrap={isClamped ? undefined : 'wrap'}
      onClick={e => e.stopPropagation()}
    >
      {visibleOptions.map(({ id, values }) => {
        const color = swatchColorFromColorway(values);
        const isSelected = id === selectedId;
        const swatch = (
          <Box
            key={id}
            as="button"
            type="button"
            aria-label={id}
            aria-pressed={isSelected}
            onClick={() => onSelect(id)}
            width={`${swatchSize}px`}
            height={`${swatchSize}px`}
            borderRadius="50%"
            flexShrink={0}
            cursor="pointer"
            border="none"
            padding={0}
            style={{
              backgroundColor: color,
              boxShadow: isSelected ? `0 0 0 2px white, 0 0 0 4px black` : '0 0 0 1px rgba(0,0,0,0.12)',
            }}
          />
        );
        return showTooltip ? (
          <TooltipV2
            key={id}
            content={<TextV2 textTransform="capitalize">{id}</TextV2>}
            placement="top"
            onMouseLeaveDelay={0}
          >
            {swatch}
          </TooltipV2>
        ) : (
          swatch
        );
      })}
      {isClamped && (
        <TooltipV2
          content={<TextV2>{tq`View all colors`}</TextV2>}
          placement="top"
          onMouseLeaveDelay={0}
        >
          <TextV2
            as="button"
            type="button"
            aria-label={tq`View all colors`}
            onClick={onOverflowClick}
            typographyVariant="label2"
            color="mono12"
            whiteSpace="nowrap"
            flexShrink={0}
            cursor="pointer"
            border="none"
            padding={0}
            backgroundColor="transparent"
          >
            +{hiddenCount}
          </TextV2>
        </TooltipV2>
      )}
    </Flex>
  );
};
